/**
 * OutboundLogTab - View captured outbound integration messages
 *
 * Displays messages that have been sent to the destination system proxy.
 * Features:
 * - Filter by system code
 * - Filter by message type
 * - View request headers and body
 * - Auto-refresh toggle
 * - Clear messages
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  FilterList as FilterListIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import IntegrationService from '../../../services/IntegrationService';
import { useTheme as useAppTheme } from '../../../contexts/ThemeContext';

const OutboundLogTab = () => {
  const { colors } = useAppTheme();

  // State
  const [messages, setMessages] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [selectedSystem, setSelectedSystem] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Detail view
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailDialog, setDetailDialog] = useState({ open: false, message: null });

  // Clear confirmation
  const [clearDialog, setClearDialog] = useState({ open: false, systemCode: null });

  // Load systems on mount
  useEffect(() => {
    loadSystems();
  }, []);

  // Load messages when filters change
  useEffect(() => {
    loadMessages();
  }, [selectedSystem, page, rowsPerPage]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadMessages();
      }, 5000); // Refresh every 5 seconds
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [autoRefresh, selectedSystem, page, rowsPerPage]);

  const loadSystems = async () => {
    try {
      const data = await IntegrationService.listOutboundSystems();
      setSystems(data || []);
    } catch (err) {
      console.error('Failed to load systems:', err);
    }
  };

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (selectedSystem) {
        data = await IntegrationService.listOutboundMessagesBySystem(
          selectedSystem,
          page,
          rowsPerPage
        );
      } else {
        data = await IntegrationService.listOutboundMessages(page, rowsPerPage);
      }
      setMessages(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      setError(err.message);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSystem, page, rowsPerPage]);

  const handleClearMessages = async () => {
    try {
      if (clearDialog.systemCode) {
        await IntegrationService.clearOutboundMessagesBySystem(clearDialog.systemCode);
      } else {
        await IntegrationService.clearAllOutboundMessages();
      }
      setClearDialog({ open: false, systemCode: null });
      loadMessages();
      loadSystems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyBody = (body) => {
    navigator.clipboard.writeText(body || '');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatJson = (jsonStr) => {
    if (!jsonStr) return '';
    try {
      const parsed = JSON.parse(jsonStr);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonStr;
    }
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return colors.success || '#4caf50';
    if (status >= 400 && status < 500) return colors.warning || '#ff9800';
    if (status >= 500) return colors.error || '#f44336';
    return colors.textSecondary;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', bgcolor: colors.background }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          Outbound Message Log
        </Typography>

        {/* System Filter */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>Filter by System</InputLabel>
          <Select
            value={selectedSystem}
            onChange={(e) => {
              setSelectedSystem(e.target.value);
              setPage(0);
            }}
            label="Filter by System"
            sx={{
              color: colors.textPrimary,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
            }}
          >
            <MenuItem value="">All Systems</MenuItem>
            {systems.map((sys) => (
              <MenuItem key={sys.systemCode} value={sys.systemCode}>
                {sys.systemCode} ({sys.messageCount})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Auto-refresh toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: colors.primary,
                },
              }}
            />
          }
          label={<Typography variant="body2" sx={{ color: colors.textSecondary }}>Auto-refresh</Typography>}
        />

        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Tooltip title="Refresh">
          <IconButton onClick={loadMessages} disabled={loading} sx={{ color: colors.textSecondary }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => setClearDialog({ open: true, systemCode: selectedSystem || null })}
          sx={{
            color: colors.error,
            borderColor: colors.error,
            '&:hover': { borderColor: colors.error, bgcolor: `${colors.error}10` },
          }}
        >
          Clear {selectedSystem ? selectedSystem : 'All'}
        </Button>
      </Box>

      {/* Error display */}
      {error && (
        <Box sx={{ p: 2, bgcolor: `${colors.error}20`, borderBottom: `1px solid ${colors.error}` }}>
          <Typography sx={{ color: colors.error }}>{error}</Typography>
        </Box>
      )}

      {/* Message Table */}
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600, width: 40 }} />
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600 }}>
                Timestamp
              </TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600 }}>
                System
              </TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600 }}>
                Message Type
              </TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600 }}>
                Method
              </TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600 }}>
                Status
              </TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textPrimary, fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ color: colors.primary }} />
                </TableCell>
              </TableRow>
            ) : messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: colors.textSecondary }}>
                  No messages found
                </TableCell>
              </TableRow>
            ) : (
              messages.map((msg) => (
                <React.Fragment key={msg.id}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: `${colors.primary}10` },
                    }}
                  >
                    <TableCell sx={{ color: colors.textSecondary }}>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedRow(expandedRow === msg.id ? null : msg.id)}
                        sx={{ color: colors.textSecondary }}
                      >
                        {expandedRow === msg.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
                      {formatDate(msg.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={msg.systemCode}
                        size="small"
                        sx={{
                          bgcolor: `${colors.primary}20`,
                          color: colors.primary,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.textPrimary }}>
                      {msg.messageType || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={msg.httpMethod}
                        size="small"
                        sx={{
                          bgcolor: colors.surface,
                          color: colors.textSecondary,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {msg.responseStatus >= 200 && msg.responseStatus < 300 ? (
                          <SuccessIcon sx={{ fontSize: 16, color: colors.success || '#4caf50' }} />
                        ) : (
                          <ErrorIcon sx={{ fontSize: 16, color: getStatusColor(msg.responseStatus) }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{ color: getStatusColor(msg.responseStatus), fontWeight: 500 }}
                        >
                          {msg.responseStatus}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setDetailDialog({ open: true, message: msg })}
                          sx={{ color: colors.textSecondary }}
                        >
                          <FilterListIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy Body">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyBody(msg.requestBody)}
                          sx={{ color: colors.textSecondary }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expanded row */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedRow === msg.id ? `1px solid ${colors.border}` : 'none' }}>
                      <Collapse in={expandedRow === msg.id}>
                        <Box sx={{ p: 2, bgcolor: colors.surface }}>
                          <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1 }}>
                            Request Body:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              bgcolor: colors.background,
                              p: 1.5,
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: 200,
                              fontSize: '0.8rem',
                              color: colors.textSecondary,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            {formatJson(msg.requestBody) || '(empty)'}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{
          borderTop: `1px solid ${colors.border}`,
          color: colors.textSecondary,
          '& .MuiTablePagination-selectIcon': { color: colors.textSecondary },
        }}
      />

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, message: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
          Message Details
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {detailDialog.message && (
            <Box sx={{ p: 2 }}>
              {/* Metadata */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>ID</Typography>
                  <Typography sx={{ color: colors.textPrimary }}>{detailDialog.message.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>Timestamp</Typography>
                  <Typography sx={{ color: colors.textPrimary }}>
                    {formatDate(detailDialog.message.createdAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>System</Typography>
                  <Typography sx={{ color: colors.textPrimary }}>{detailDialog.message.systemCode}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>Message Type</Typography>
                  <Typography sx={{ color: colors.textPrimary }}>
                    {detailDialog.message.messageType || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>Tenant ID</Typography>
                  <Typography sx={{ color: colors.textPrimary }}>
                    {detailDialog.message.tenantId || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>User ID</Typography>
                  <Typography sx={{ color: colors.textPrimary }}>
                    {detailDialog.message.userId || '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Headers */}
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1 }}>
                Request Headers:
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: colors.surface,
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 150,
                  fontSize: '0.8rem',
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                  mb: 2,
                }}
              >
                {formatJson(detailDialog.message.requestHeaders) || '(none)'}
              </Box>

              {/* Body */}
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1 }}>
                Request Body:
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: colors.surface,
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 250,
                  fontSize: '0.8rem',
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {formatJson(detailDialog.message.requestBody) || '(empty)'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            startIcon={<CopyIcon />}
            onClick={() => handleCopyBody(detailDialog.message?.requestBody)}
            sx={{ color: colors.textSecondary }}
          >
            Copy Body
          </Button>
          <Button
            variant="contained"
            onClick={() => setDetailDialog({ open: false, message: null })}
            sx={{ bgcolor: colors.primary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Confirmation Dialog */}
      <Dialog
        open={clearDialog.open}
        onClose={() => setClearDialog({ open: false, systemCode: null })}
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Clear Messages?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textSecondary }}>
            {clearDialog.systemCode
              ? `This will delete all messages for ${clearDialog.systemCode}. This action cannot be undone.`
              : 'This will delete all captured messages. This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setClearDialog({ open: false, systemCode: null })} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleClearMessages}
            sx={{ bgcolor: colors.error, '&:hover': { bgcolor: colors.error } }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutboundLogTab;
